/** @jsxImportSource @emotion/react */
import styled from "@emotion/styled";
import React, { useEffect, useState } from "react";
import UI from "@mile-meeting/mile-ui";
import Chart from "react-apexcharts";
import ko from "apexcharts/dist/locales/ko.json";
import { ApexOptions } from "apexcharts";
import { TDailyVisitorType } from "types/analytics.types";

const Container = styled.div`
  width: 360px;
  height: 480px;
  background-color: var(--alias-color-accent-light-pink);
  border-radius: 12px;
  box-sizing: border-box;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
`;

const ContentContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const WalkinVisitTypeCountPieChart = ({
  month,
  monthlyDailyVisitorTypes,
}: {
  month: number;
  monthlyDailyVisitorTypes?: TDailyVisitorType[];
}) => {
  const monthlyVisitPurposeNames: string[] = (monthlyDailyVisitorTypes ?? []).map((visitType) => visitType.purposeName);
  const monthlyVisitCounts: number[] = (monthlyDailyVisitorTypes ?? []).map((visitType) => visitType.visitorCount);

  const [walkinVisitChartOptions, setWalkinVisitChartOptions] = useState<Object | null>(null);
  const [walkinVisitSeries, setWalkinVisitSeries] = useState<ApexOptions["series"]>([]);

  let chartOptions = {
    locales: [ko],
    defaultLocale: "ko",
    labels: monthlyVisitPurposeNames,
    series: monthlyVisitCounts,
    colors: [
      "var(--global-color-blue-60)",
      "var(--global-color-skyblue-60)",
      "var(--global-color-cyan-60)",
      "var(--global-color-lime-60)",
      "var(--global-color-yellow-70)",
      "var(--global-color-orange-70)",
      "var(--global-color-pink-70)",
      "var(--global-color-purple-70)",
    ],
    chart: {
      type: "donut",
      width: 322,
      height: 376,
      animations: {
        enabled: false, // 창 크기 변경에 의한 리렌더링 비활성화
      },
      zoom: {
        enabled: false,
      },
    },
    plotOptions: {
      pie: {
        donut: {
          width: 71, // 원통 너비 설정
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    legend: {
      position: "bottom",
      fontSize: "12px",
      fontWeight: 400,
      labels: {
        offsetX: -14,
        offsetY: 51,
        colors: "#373D3F",
        useSeriesColors: false,
      },
      itemMargin: {
        horizontal: 7,
      },
      markers: {
        fillColors: [
          "var(--global-color-blue-60)",
          "var(--global-color-skyblue-60)",
          "var(--global-color-cyan-60)",
          "var(--global-color-lime-60)",
          "var(--global-color-yellow-70)",
          "var(--global-color-orange-70)",
          "var(--global-color-pink-70)",
          "var(--global-color-purple-70)",
        ],
      },
    },
  };

  useEffect(() => {
    setWalkinVisitSeries(monthlyVisitCounts);
    setWalkinVisitChartOptions(chartOptions);
  }, [monthlyDailyVisitorTypes]);

 
  return (
    <Container>
      <TitleContainer>
        <UI.Tag label={`${month}월`} type="secondary" color="pink" />
        <UI.Typography type="body01 normal" fontStyle="medium" color="var(--alias-color-label-normal)">
          당일 방문 현황
        </UI.Typography>
      </TitleContainer>
      <ContentContainer>
        {(walkinVisitSeries?.length ?? 0) > 0 ? (
          walkinVisitChartOptions ? (
            <Chart options={walkinVisitChartOptions} series={walkinVisitSeries} type="donut" height={376} />
          ) : (
            <></>
          )
        ) : (
          <UI.Typography type="title01" fontStyle="medium" color="var(--alias-color-label-strong)">
            N/D
          </UI.Typography>
        )}
      </ContentContainer>
    </Container>
  );
};

export default WalkinVisitTypeCountPieChart;
